import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { rbacApi, iamApi, authTenantServiceApi, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import RoleMappingFields from "@/components/RoleMappingFields";
import ReadOnlyNotice from "@/components/ReadOnlyNotice";
import { defaultFeatureConfig } from "@/lib/tenant-theme";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  UserPlus,
  Mail,
  ShieldCheck,
} from "lucide-react";

/**
 * Users & Roles. Two sections against the central Auth platform:
 *
 *  1. USERS (tis-auth-iam-service, iamApi) — a tenant user directory with
 *     invite (create PENDING + activation token), edit profile, resend
 *     invite, and lifecycle status transitions (deactivate/suspend/
 *     reactivate). "Delete" is deliberately a soft DEACTIVATE — the
 *     platform offboards, it does not hard-delete people.
 *  2. ROLES (tis-auth-rbac-service, rbacApi) — the role list plus full
 *     custom-role CRUD (create / edit-permissions / delete) and the
 *     unchanged role -> per-portal access mapping (RoleMappingFields,
 *     persisted to Auth config featureFlags.roleMapping). System roles
 *     show a System badge and expose no edit/delete.
 *
 * Every edit control is gated on isAdmin() (OWN/TSA/platform operator),
 * exactly like the original Save button; non-admins keep the
 * ReadOnlyNotice and see no mutating controls. Server-side authorization
 * (IAM_USER_MANAGE / IAM_ROLE_MANAGE) is the real enforcement — this is UX.
 *
 * NOTE: per-user role assignment is wired via the ManageRolesDialog
 * ("Manage roles" per-user action): GET /users/:id/roles lists current
 * grants, POST /users/:id/roles assigns a tenant-scoped role, and DELETE
 * /users/:id/roles/:assignmentId revokes one. The backend enforces
 * no-escalation (RBAC-11) — a caller cannot grant a role they do not hold
 * — which surfaces as a 403 toast here. Unit/team-scoped grants remain a
 * later refinement (scope is TENANT here).
 */

const STATUS_BADGE = {
  ACTIVE: "success",
  PENDING: "secondary",
  SUSPENDED: "outline",
  DEACTIVATED: "destructive",
};

export default function AdminRoles() {
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  return (
    <Layout variant="tenant">
      <h1 className="font-heading text-2xl font-bold">Users &amp; Roles</h1>
      <p className="mt-1 text-muted-foreground">
        Manage people and their access, via the central Auth platform.
      </p>
      <ReadOnlyNotice what="users and roles" />

      <div className="mt-6 space-y-8">
        <UsersSection admin={admin} />
        <RolesSection admin={admin} />
      </div>
    </Layout>
  );
}

/* ------------------------------------------------------------------ */
/* USERS                                                               */
/* ------------------------------------------------------------------ */

function UsersSection({ admin }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 25;

  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [confirmUser, setConfirmUser] = useState(null); // { user, action }
  const [rolesUser, setRolesUser] = useState(null); // user whose roles we manage

  const load = useCallback(() => {
    // GET /users requires IAM_USER_MANAGE — only admins may list the
    // directory. A non-admin firing it gets a 403 (and, with a stale
    // token, a 401 that the api-client interceptor would turn into a
    // logout). So skip the call entirely for non-admins; they see the
    // ReadOnlyNotice instead of an empty 403'd table.
    if (!admin) {
      setLoading(false);
      return;
    }
    setLoading(true);
    iamApi
      .get(`/users?page=${page}&pageSize=${pageSize}`)
      .then((res) => {
        setUsers(res.data?.items ?? []);
        setTotalPages(res.data?.totalPages ?? 1);
        setError("");
      })
      .catch((err) => {
        const m = errorMessage(err);
        if (m) setError(m);
      })
      .finally(() => setLoading(false));
  }, [page, admin]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>Users</CardTitle>
          <CardDescription>People in this workspace and their account status.</CardDescription>
        </div>
        {admin && (
          <Button size="sm" onClick={() => setAddOpen(true)} data-testid="user-add-btn">
            <UserPlus className="mr-2 h-4 w-4" />
            Add user
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
        {error && (
          <p className="text-sm text-destructive" data-testid="users-error">
            {error}
          </p>
        )}

        {!loading && !admin && (
          <p className="text-sm text-muted-foreground" data-testid="users-readonly">
            You have view-only access. Ask a tenant administrator to manage users.
          </p>
        )}

        {!loading && !error && admin && (
          <>
            <Table data-testid="users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  {admin && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.userId}>
                    <TableCell className="font-medium">{u.displayName || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_BADGE[u.status] || "secondary"}>{u.status}</Badge>
                    </TableCell>
                    {admin && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {u.status === "PENDING" && (
                            <ResendInviteButton user={u} />
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setRolesUser(u)}
                            aria-label={`Manage roles for ${u.email}`}
                            data-testid="user-manage-roles-btn"
                          >
                            <ShieldCheck className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditUser(u)}
                            aria-label={`Edit ${u.email}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {u.status === "SUSPENDED" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmUser({ user: u, action: "ACTIVE" })}
                            >
                              Reactivate
                            </Button>
                          )}
                          {u.status === "ACTIVE" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmUser({ user: u, action: "SUSPENDED" })}
                            >
                              Suspend
                            </Button>
                          )}
                          {u.status !== "DEACTIVATED" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              onClick={() => setConfirmUser({ user: u, action: "DEACTIVATED" })}
                              aria-label={`Deactivate ${u.email}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={admin ? 4 : 3} className="py-8 text-center text-muted-foreground">
                      No users yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>

      {admin && addOpen && (
        <AddUserDialog
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            setAddOpen(false);
            load();
          }}
        />
      )}
      {admin && editUser && (
        <EditUserDialog
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => {
            setEditUser(null);
            load();
          }}
        />
      )}
      {admin && confirmUser && (
        <ConfirmStatusDialog
          user={confirmUser.user}
          action={confirmUser.action}
          onClose={() => setConfirmUser(null)}
          onDone={() => {
            setConfirmUser(null);
            load();
          }}
        />
      )}
      {admin && rolesUser && (
        <ManageRolesDialog user={rolesUser} onClose={() => setRolesUser(null)} />
      )}
    </Card>
  );
}

function ResendInviteButton({ user }) {
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try {
      await iamApi.post(`/users/${user.userId}/resend-activation`);
      toast.success(`Invite re-sent to ${user.email}.`);
    } catch (err) {
      const m = errorMessage(err);
      if (m) toast.error(m);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button variant="ghost" size="sm" onClick={resend} disabled={busy} aria-label={`Resend invite to ${user.email}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
    </Button>
  );
}

function AddUserDialog({ onClose, onSaved }) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const body = { email: email.trim() };
      if (displayName.trim()) body.displayName = displayName.trim();
      const res = await iamApi.post("/users", body);
      // createUserResponse carries the one-time activation token in-band —
      // surface that an invite is pending so the admin knows the user must
      // activate (delivery is handled by the platform's invite flow).
      const pending = res.data?.activationToken ? " An activation invite has been issued." : "";
      toast.success(`User ${email.trim()} created.${pending}`);
      onSaved();
    } catch (err) {
      const m = errorMessage(err);
      if (m) toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add user</DialogTitle>
            <DialogDescription>
              Invites a new user. They receive an activation link and set their own password — no
              password is set here.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="add-user-email">Email</Label>
              <Input
                id="add-user-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="person@company.com"
                data-testid="add-user-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="add-user-name">Display name</Label>
              <Input
                id="add-user-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jane Doe"
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !email.trim()} data-testid="add-user-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send invite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [email, setEmail] = useState(user.email || "");
  const [displayName, setDisplayName] = useState(user.displayName || "");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {};
      if (email.trim() && email.trim() !== user.email) body.email = email.trim();
      if (displayName.trim() !== (user.displayName || "")) body.displayName = displayName.trim();
      if (Object.keys(body).length === 0) {
        onClose();
        return;
      }
      await iamApi.patch(`/users/${user.userId}`, body);
      toast.success("User updated.");
      onSaved();
    } catch (err) {
      const m = errorMessage(err);
      if (m) toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>Update this user&apos;s profile details.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="edit-user-email">Email</Label>
              <Input
                id="edit-user-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-user-name">Display name</Label>
              <Input
                id="edit-user-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_COPY = {
  DEACTIVATED: {
    title: "Deactivate user",
    body: (email) =>
      `Deactivate ${email}? They lose access immediately. Deactivation is permanent — to restore access you must re-invite them.`,
    confirm: "Deactivate",
    variant: "destructive",
  },
  SUSPENDED: {
    title: "Suspend user",
    body: (email) => `Suspend ${email}? They are temporarily blocked from signing in. You can reactivate them later.`,
    confirm: "Suspend",
    variant: "default",
  },
  ACTIVE: {
    title: "Reactivate user",
    body: (email) => `Reactivate ${email}? They regain access and can sign in again.`,
    confirm: "Reactivate",
    variant: "default",
  },
};

function ConfirmStatusDialog({ user, action, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const copy = STATUS_COPY[action];

  const apply = async () => {
    setBusy(true);
    try {
      await iamApi.patch(`/users/${user.userId}/status`, { status: action });
      toast.success(`${user.email} — ${copy.confirm.toLowerCase()}d.`);
      onDone();
    } catch (err) {
      const m = errorMessage(err);
      if (m) toast.error(m); // surfaces the backend's valid-transition 409
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{copy.title}</DialogTitle>
          <DialogDescription>{copy.body(user.email)}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant={copy.variant} onClick={apply} disabled={busy} data-testid="confirm-status-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {copy.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Per-user role assignment. Lists the user's current role assignments
 * (GET /users/:id/roles), assigns a tenant-scoped role (POST
 * /users/:id/roles with role_code + scope TENANT), and revokes one
 * (DELETE /users/:id/roles/:assignmentId). The backend enforces
 * "no escalation" (RBAC-11): a caller cannot grant a role they do not
 * themselves hold — that surfaces as a 403, shown here as a toast, not
 * a crash. Roles are tenant-scoped here (scope TENANT); unit/team-scoped
 * grants are a later refinement.
 */
function ManageRolesDialog({ user, onClose }) {
  const [assignments, setAssignments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCode, setSelectedCode] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [revokingId, setRevokingId] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      rbacApi.get(`/users/${user.userId}/roles`),
      rbacApi.get("/roles"),
    ])
      .then(([aRes, rRes]) => {
        setAssignments(aRes.data ?? []);
        setRoles(rRes.data ?? []);
      })
      .catch((err) => {
        const m = errorMessage(err);
        if (m) toast.error(m);
      })
      .finally(() => setLoading(false));
  }, [user.userId]);

  useEffect(() => {
    load();
  }, [load]);

  // Roles not already assigned to this user, offered in the dropdown.
  const assignedCodes = new Set(assignments.map((a) => a.roleCode));
  const assignable = roles.filter((r) => !assignedCodes.has(r.code));

  const assign = async () => {
    if (!selectedCode) return;
    setAssigning(true);
    try {
      await rbacApi.post(`/users/${user.userId}/roles`, {
        role_code: selectedCode,
        scope: { type: "TENANT" },
      });
      toast.success(`Role ${selectedCode} assigned to ${user.email}.`);
      setSelectedCode("");
      load();
    } catch (err) {
      const m = errorMessage(err);
      if (m) toast.error(m);
    } finally {
      setAssigning(false);
    }
  };

  const revoke = async (assignmentId, roleCode) => {
    setRevokingId(assignmentId);
    try {
      await rbacApi.delete(`/users/${user.userId}/roles/${assignmentId}`);
      toast.success(`Role ${roleCode} revoked from ${user.email}.`);
      load();
    } catch (err) {
      const m = errorMessage(err);
      if (m) toast.error(m);
    } finally {
      setRevokingId("");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="manage-roles-dialog">
        <DialogHeader>
          <DialogTitle>Manage roles</DialogTitle>
          <DialogDescription>
            Assign or revoke roles for {user.displayName || user.email}.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <Loader2 className="my-6 h-6 w-6 animate-spin text-primary" />
        ) : (
          <div className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                Current roles
              </Label>
              <div className="mt-2 space-y-2" data-testid="assigned-roles">
                {assignments.length === 0 && (
                  <p className="text-sm text-muted-foreground">No roles assigned.</p>
                )}
                {assignments.map((a) => (
                  <div
                    key={a.assignmentId}
                    className="flex items-center justify-between rounded-md border px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{a.roleCode}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {a.scope?.type || "TENANT"}
                        {a.expiresAt ? ` · expires ${new Date(a.expiresAt).toLocaleDateString()}` : ""}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      disabled={revokingId === a.assignmentId}
                      onClick={() => revoke(a.assignmentId, a.roleCode)}
                      aria-label={`Revoke ${a.roleCode}`}
                    >
                      {revokingId === a.assignmentId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="assign-role-select" className="text-xs uppercase tracking-wide text-muted-foreground">
                Add a role
              </Label>
              <div className="mt-2 flex items-center gap-2">
                <select
                  id="assign-role-select"
                  value={selectedCode}
                  onChange={(e) => setSelectedCode(e.target.value)}
                  data-testid="assign-role-select"
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="">Select a role…</option>
                  {assignable.map((r) => (
                    <option key={r.roleId} value={r.code}>
                      {r.name} ({r.code})
                    </option>
                  ))}
                </select>
                <Button
                  onClick={assign}
                  disabled={!selectedCode || assigning}
                  data-testid="assign-role-btn"
                >
                  {assigning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Assign
                </Button>
              </div>
              {assignable.length === 0 && roles.length > 0 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  This user already holds every available role.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* ROLES                                                               */
/* ------------------------------------------------------------------ */

function RolesSection({ admin }) {
  const { user } = useAuth();
  const tenantId = user?.tenantId;

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [featureConfig, setFeatureConfig] = useState(defaultFeatureConfig());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [deleteRole, setDeleteRole] = useState(null);

  const loadRoles = useCallback(() => {
    setLoading(true);
    rbacApi
      .get("/roles")
      .then((res) => {
        setRoles(res.data);
        setError("");
      })
      .catch((err) => {
        const m = errorMessage(err);
        if (m) setError(m);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    rbacApi
      .get("/permissions")
      .then((res) => setPermissions(res.data ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    authTenantServiceApi
      .get(`/api/v1/tenants/${tenantId}/configuration`)
      .then((res) =>
        setFeatureConfig({ ...defaultFeatureConfig(), ...(res.data?.featureFlags || {}) }),
      )
      .catch(() => {});
  }, [tenantId]);

  const saveMapping = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await authTenantServiceApi.patch(`/api/v1/tenants/${tenantId}/configuration`, {
        featureFlags: featureConfig,
      });
      setSaved(true);
      toast.success("Role mapping saved.");
    } catch (err) {
      const m = errorMessage(err);
      if (m) {
        setError(m);
        toast.error(m);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>Roles</CardTitle>
            <CardDescription>
              Available to this workspace from tis-auth-rbac-service.
            </CardDescription>
          </div>
          {admin && (
            <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="role-create-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create role
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3" data-testid="roles-list">
          {loading && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
          {error && (
            <p className="text-sm text-destructive" data-testid="roles-error">
              {error}
            </p>
          )}
          {!loading &&
            roles.map((role) => (
              <div
                key={role.roleId}
                className="flex items-center justify-between border-b py-2 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{role.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {role.code} · {role.permissions?.length ?? 0} permissions
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {role.isSystem ? (
                    <Badge variant="secondary">System</Badge>
                  ) : (
                    admin && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditRole(role)}
                          aria-label={`Edit permissions for ${role.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeleteRole(role)}
                          aria-label={`Delete ${role.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )
                  )}
                </div>
              </div>
            ))}
          {!loading && roles.length === 0 && (
            <p className="text-sm text-muted-foreground">No roles found.</p>
          )}
        </CardContent>
      </Card>

      <RoleMappingFields featureConfig={featureConfig} onChange={setFeatureConfig} />

      {admin && (
        <div className="flex items-center gap-3">
          <Button onClick={saveMapping} disabled={saving} data-testid="roles-save-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save role mapping
          </Button>
          {saved && (
            <p className="text-sm text-emerald-600" data-testid="roles-saved-message">
              Saved.
            </p>
          )}
        </div>
      )}

      {admin && createOpen && (
        <CreateRoleDialog
          permissions={permissions}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            loadRoles();
          }}
        />
      )}
      {admin && editRole && (
        <EditRoleDialog
          role={editRole}
          permissions={permissions}
          onClose={() => setEditRole(null)}
          onSaved={() => {
            setEditRole(null);
            loadRoles();
          }}
        />
      )}
      {admin && deleteRole && (
        <DeleteRoleDialog
          role={deleteRole}
          onClose={() => setDeleteRole(null)}
          onDone={() => {
            setDeleteRole(null);
            loadRoles();
          }}
        />
      )}
    </div>
  );
}

/** Multi-select of permission keys, rendered as a scrollable checkbox
 * list — no extra ui primitive needed and it reads clearly for long
 * permission catalogs. */
function PermissionPicker({ permissions, selected, onToggle }) {
  return (
    <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
      {permissions.length === 0 && (
        <p className="px-1 py-2 text-sm text-muted-foreground">No permissions available.</p>
      )}
      {permissions.map((p) => {
        const key = p.key;
        const checked = selected.has(key);
        return (
          <label
            key={key}
            className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-sm hover:bg-muted/50"
          >
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-primary"
              checked={checked}
              onChange={() => onToggle(key)}
            />
            <span>
              <span className="font-medium">{key}</span>
              {p.description && (
                <span className="block text-xs text-muted-foreground">{p.description}</span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function CreateRoleDialog({ permissions, onClose, onSaved }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !name.trim() || selected.size === 0) return;
    setBusy(true);
    try {
      const body = {
        code: code.trim().toUpperCase(),
        name: name.trim(),
        permissionKeys: [...selected],
      };
      if (description.trim()) body.description = description.trim();
      await rbacApi.post("/roles", body);
      toast.success(`Role ${body.code} created.`);
      onSaved();
    } catch (err) {
      const m = errorMessage(err);
      if (m) toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Create role</DialogTitle>
            <DialogDescription>
              Define a custom role and the permissions it grants.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="role-code">Code</Label>
                <Input
                  id="role-code"
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="OPS_LEAD"
                  data-testid="role-code"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="role-name">Name</Label>
                <Input
                  id="role-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ops Lead"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="role-desc">Description</Label>
              <Input
                id="role-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Permissions ({selected.size} selected)</Label>
              <PermissionPicker permissions={permissions} selected={selected} onToggle={toggle} />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !code.trim() || !name.trim() || selected.size === 0}
              data-testid="role-create-submit"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create role
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditRoleDialog({ role, permissions, onClose, onSaved }) {
  const [selected, setSelected] = useState(new Set(role.permissions ?? []));
  const [busy, setBusy] = useState(false);

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await rbacApi.patch(`/roles/${role.roleId}/permissions`, {
        permissionKeys: [...selected],
      });
      toast.success(`Permissions updated for ${role.name}.`);
      onSaved();
    } catch (err) {
      const m = errorMessage(err);
      if (m) toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Edit permissions — {role.name}</DialogTitle>
            <DialogDescription>Choose the permissions this role grants.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-1.5">
            <Label>Permissions ({selected.size} selected)</Label>
            <PermissionPicker permissions={permissions} selected={selected} onToggle={toggle} />
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="role-edit-submit">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRoleDialog({ role, onClose, onDone }) {
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await rbacApi.delete(`/roles/${role.roleId}`);
      toast.success(`Role ${role.name} deleted.`);
      onDone();
    } catch (err) {
      // Surfaces 409 'still assigned' / 'system role' as a readable toast.
      const m = errorMessage(err);
      if (m) toast.error(m);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete role</DialogTitle>
          <DialogDescription>
            Delete the role <span className="font-medium text-foreground">{role.name}</span>? If it
            is still assigned to any user, deletion is refused — revoke the assignments first.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={remove} disabled={busy} data-testid="role-delete-btn">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
